<div class="general-container settings-page">
  <p-dialog
    [header]="'SETTINGS_GENERAL.QR_CODE_DIALOG_TITLE' | translate"
    [visible]="qrCodeDialogVisible()"
    (visibleChange)="qrCodeDialogVisible.set($event)"
    [modal]="true"
    [closable]="true"
    [draggable]="false"
    styleClass="qr-code-dialog">
    @if (qrCodeDataUrl()) {
      <div class="qr-code-container">
        <img
          [src]="qrCodeDataUrl()!"
          [alt]="'SETTINGS_GENERAL.QR_CODE_ALT' | translate"
          class="qr-code-image" />
      </div>
    }
  </p-dialog>

  <div class="settings-page__content">
    <p class="settings-page__description">
      {{ 'SETTINGS_GENERAL.DESCRIPTION' | translate }}
    </p>

    <div class="settings-content">
      <div class="settings-section">
        <label for="tenant-id-code" class="settings-page__label">
          {{ 'SETTINGS_GENERAL.TENANT_ID' | translate }}
        </label>
        <p class="description">
          {{ 'SETTINGS_GENERAL.TENANT_ID_DESCRIPTION' | translate }}
        </p>
        <div class="tenant-id-display">
          <code id="tenant-id-code" class="tenant-id-code tf-ltr-token">
            {{ tenantId() || ('GENERAL.LOADING' | translate) }}
          </code>
          @if (tenantId()) {
            <p-button
              icon="pi pi-copy"
              [text]="true"
              severity="secondary"
              [cdkCopyToClipboard]="tenantId()!"
              [pTooltip]="'SETTINGS_GENERAL.COPY_TENANT_ID' | translate"
              tooltipPosition="top"
              [attr.aria-label]="'SETTINGS_GENERAL.COPY_TENANT_ID' | translate"></p-button>
            <p-button
              icon="pi pi-qrcode"
              [text]="true"
              severity="secondary"
              (onClick)="showTenantIdQrCode()"
              [pTooltip]="'SETTINGS_GENERAL.SHOW_QR_CODE' | translate"
              tooltipPosition="top"
              [attr.aria-label]="'SETTINGS_GENERAL.SHOW_QR_CODE' | translate"></p-button>
          }
        </div>
      </div>
    </div>

    @if (loadError(); as error) {
      <tf-settings-load-error
        [title]="error"
        [message]="'SETTINGS_GENERAL.ERROR.LOAD_MESSAGE' | translate"
        [details]="loadErrorDetails()"
        [retrying]="isLoading()"
        (retry)="retryLoad()" />
    } @else if (isLoading()) {
      <div class="general-loading-skeleton" aria-hidden="true">
        <div class="general-loading-skeleton__section">
          <div class="general-loading-skeleton__label"></div>
          <div class="general-loading-skeleton__control"></div>
        </div>
        <div class="general-loading-skeleton__grid">
          <div class="general-loading-skeleton__section">
            <div class="general-loading-skeleton__label"></div>
            <div class="general-loading-skeleton__text"></div>
            <div class="general-loading-skeleton__control"></div>
          </div>
          <div class="general-loading-skeleton__section">
            <div class="general-loading-skeleton__label"></div>
            <div class="general-loading-skeleton__text"></div>
            <div class="general-loading-skeleton__control"></div>
          </div>
        </div>
        <div class="general-loading-skeleton__section">
          <div class="general-loading-skeleton__label"></div>
          <div class="general-loading-skeleton__text general-loading-skeleton__text--wide"></div>
          <div class="general-loading-skeleton__card"></div>
          <div class="general-loading-skeleton__card"></div>
        </div>
        <div class="general-loading-skeleton__grid">
          <div class="general-loading-skeleton__section">
            <div class="general-loading-skeleton__label"></div>
            <div class="general-loading-skeleton__text"></div>
            <div class="general-loading-skeleton__pills"></div>
          </div>
          <div class="general-loading-skeleton__section">
            <div class="general-loading-skeleton__label"></div>
            <div class="general-loading-skeleton__text"></div>
            <div class="general-loading-skeleton__pills general-loading-skeleton__pills--short"></div>
          </div>
        </div>
      </div>
    } @else {
      <tf-settings-inline-message
        [message]="saveError()"
        messageId="general-settings-save-error"
        (dismissed)="clearSaveError()" />

      <div class="settings-content">
      <!-- Tenant Name Section -->

      <form class="tenant-name-form" (submit)="$event.preventDefault()">
        <div class="form-field settings-page__field">
          <label for="tenantName" class="settings-page__label">
            {{ 'SETTINGS_GENERAL.TENANT_NAME' | translate }}
          </label>
          <div class="settings-page__field-input-wrap">
            <input
              pInputText
              id="tenantName"
              [formControl]="tenantNameControl"
              [placeholder]="'SETTINGS_GENERAL.TENANT_NAME_PLACEHOLDER' | translate"
              class="w-full"
              [class.ng-invalid]="!!tenantServerErrors.value()['tenantName']"
              [attr.aria-invalid]="tenantServerErrors.value()['tenantName'] ? true : null"
              [attr.aria-describedby]="
                tenantServerErrors.value()['tenantName'] ? 'tenantName-error' : null
              " />
          </div>
          @if (tenantNameControl.invalid && tenantNameControl.touched) {
            <small class="error">
              @if (tenantNameControl.errors?.['required']) {
                {{ 'SETTINGS_GENERAL.VALIDATION.REQUIRED' | translate }}
              }
              @if (tenantNameControl.errors?.['maxlength']) {
                {{ 'SETTINGS_GENERAL.VALIDATION.MAX_LENGTH' | translate }}
              }
            </small>
          }
          @if (tenantServerErrors.value()['tenantName']; as error) {
            <small class="error" id="tenantName-error" role="alert">{{ error }}</small>
          }
        </div>

        <div class="form-field settings-page__field">
          <label
            for="language"
            class="settings-page__label"
            tabindex="-1"
            (click)="languageSelect.show()"
            (keydown.enter)="languageSelect.show()">
            {{ 'SETTINGS_GENERAL.LANGUAGE' | translate }}
          </label>
          <p class="description">
            {{ 'SETTINGS_GENERAL.LANGUAGE_DESCRIPTION' | translate }}
          </p>
          <div class="settings-page__field-input-wrap">
            <p-select
              #languageSelect
              inputId="language"
              [options]="languageOptions"
              optionLabel="label"
              optionValue="value"
              [formControl]="languageControl"
              [placeholder]="'SETTINGS_GENERAL.LANGUAGE_PLACEHOLDER' | translate"
              class="w-full"
              [class.ng-invalid]="!!tenantServerErrors.value()['language']"></p-select>
          </div>
          @if (languageControl.invalid && languageControl.touched) {
            <small class="error">
              @if (languageControl.errors?.['required']) {
                {{ 'SETTINGS_GENERAL.VALIDATION.LANGUAGE_REQUIRED' | translate }}
              }
              @if (languageControl.errors?.['maxlength']) {
                {{ 'SETTINGS_GENERAL.VALIDATION.LANGUAGE_MAX_LENGTH' | translate }}
              }
            </small>
          }
          @if (tenantServerErrors.value()['language']; as error) {
            <small class="error" id="language-error" role="alert">{{ error }}</small>
          }
        </div>

        <div class="form-field settings-page__field">
          <label
            for="timezone"
            class="settings-page__label"
            tabindex="-1"
            (click)="timezoneSelect.show()"
            (keydown.enter)="timezoneSelect.show()">
            {{ 'SETTINGS_GENERAL.TIMEZONE' | translate }}
          </label>
          <p class="description">
            {{ 'SETTINGS_GENERAL.TIMEZONE_DESCRIPTION' | translate }}
          </p>
          <div class="settings-page__field-input-wrap">
            <p-select
              #timezoneSelect
              inputId="timezone"
              [options]="timezoneOptions"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              [formControl]="timezoneControl"
              [placeholder]="'SETTINGS_GENERAL.TIMEZONE_PLACEHOLDER' | translate"
              class="w-full"
              [class.ng-invalid]="!!tenantServerErrors.value()['timezone']"></p-select>
          </div>
          @if (timezoneControl.invalid && timezoneControl.touched) {
            <small class="error">
              @if (timezoneControl.errors?.['required']) {
                {{ 'SETTINGS_GENERAL.VALIDATION.TIMEZONE_REQUIRED' | translate }}
              }
              @if (timezoneControl.errors?.['maxlength']) {
                {{ 'SETTINGS_GENERAL.VALIDATION.TIMEZONE_MAX_LENGTH' | translate }}
              }
            </small>
          }
          @if (tenantServerErrors.value()['timezone']; as error) {
            <small class="error" id="timezone-error" role="alert">{{ error }}</small>
          }
        </div>
      </form>

      <div class="settings-section">
        <span class="settings-page__label">
          {{ 'SETTINGS_GENERAL.BRANDING' | translate }}
        </span>
        <p class="description">
          {{ 'SETTINGS_GENERAL.BRANDING_DESCRIPTION' | translate }}
        </p>

        <div class="branding-list">
          <div class="branding-card">
            <div class="branding-card__preview">
              @if (logoPreviewUrl()) {
                <img
                  [src]="logoPreviewUrl()!"
                  [alt]="'SETTINGS_GENERAL.LOGO' | translate"
                  class="branding-image branding-image--logo" />
              } @else {
                <div class="branding-placeholder" aria-hidden="true">
                  <i class="pi pi-image"></i>
                </div>
              }
            </div>
            <div class="branding-card__content">
              <div class="branding-card__title">
                {{ 'SETTINGS_GENERAL.LOGO' | translate }}
              </div>
              <div class="branding-card__description">
                {{
                  (currentLogoUrl()
                    ? 'SETTINGS_GENERAL.LOGO_CONFIGURED'
                    : 'SETTINGS_GENERAL.LOGO_DEFAULT_IN_USE'
                  ) | translate
                }}
              </div>
              @if (logoUploadStatusMessage()) {
                <small class="branding-status">{{ logoUploadStatusMessage() }}</small>
              }
              @if (logoUploadErrorMessage(); as error) {
                <small class="branding-error" role="alert">{{ error }}</small>
              }
            </div>
            <div class="branding-card__actions">
              <input
                #logoUploadInput
                type="file"
                accept="image/png,image/jpeg"
                (change)="onLogoSelected($event)"
                class="branding-upload-input" />
              <p-button
                [label]="'SETTINGS_GENERAL.UPLOAD_LOGO' | translate"
                icon="pi pi-upload"
                severity="secondary"
                [outlined]="true"
                [disabled]="isLoading() || isSaving() || !canUpdate()"
                [loading]="isSaving()"
                [pTooltip]="canUpdate() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                tooltipPosition="top"
                (onClick)="logoUploadInput.click()"></p-button>
              @if (logoMarkedForRemoval()) {
                <p-button
                  [label]="'SETTINGS_GENERAL.KEEP_LOGO' | translate"
                  icon="pi pi-undo"
                  severity="secondary"
                  [outlined]="true"
                  [disabled]="isLoading() || isSaving() || !canUpdate()"
                  [pTooltip]="canUpdate() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="undoRemoveLogo()"></p-button>
              } @else if (currentLogoUrl()) {
                <p-button
                  [label]="'SETTINGS_GENERAL.REMOVE_LOGO' | translate"
                  icon="pi pi-trash"
                  severity="danger"
                  [outlined]="true"
                  [disabled]="isLoading() || isSaving() || !canUpdate()"
                  [pTooltip]="canUpdate() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="removeLogo()"></p-button>
              }
            </div>
            @if (pendingLogoFile()) {
              <div class="branding-selection-row">
                <span>{{ pendingLogoFile()?.name }}</span>
                <p-button
                  icon="pi pi-times"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  [disabled]="isLoading() || isSaving() || !canUpdate()"
                  [pTooltip]="
                    canUpdate()
                      ? ('GENERAL.CLEAR' | translate)
                      : ('GENERAL.NO_PERMISSION' | translate)
                  "
                  tooltipPosition="top"
                  (onClick)="clearPendingLogoSelection()"></p-button>
              </div>
            }
          </div>

          <div class="branding-card">
            <div class="branding-card__preview">
              @if (faviconPreviewUrl()) {
                <img
                  [src]="faviconPreviewUrl()!"
                  [alt]="'SETTINGS_GENERAL.FAVICON' | translate"
                  class="branding-image branding-image--favicon" />
              } @else {
                <div class="branding-placeholder" aria-hidden="true">
                  <i class="pi pi-image"></i>
                </div>
              }
            </div>
            <div class="branding-card__content">
              <div class="branding-card__title">
                {{ 'SETTINGS_GENERAL.FAVICON' | translate }}
              </div>
              <div class="branding-card__description">
                {{
                  (currentFaviconUrl()
                    ? 'SETTINGS_GENERAL.FAVICON_CONFIGURED'
                    : 'SETTINGS_GENERAL.FAVICON_DEFAULT_IN_USE'
                  ) | translate
                }}
              </div>
              @if (faviconUploadStatusMessage()) {
                <small class="branding-status">{{ faviconUploadStatusMessage() }}</small>
              }
              @if (faviconUploadErrorMessage(); as error) {
                <small class="branding-error" role="alert">{{ error }}</small>
              }
            </div>
            <div class="branding-card__actions">
              <input
                #faviconUploadInput
                type="file"
                accept="image/png,image/jpeg"
                (change)="onFaviconSelected($event)"
                class="branding-upload-input" />
              <p-button
                [label]="'SETTINGS_GENERAL.UPLOAD_FAVICON' | translate"
                icon="pi pi-upload"
                severity="secondary"
                [outlined]="true"
                [disabled]="isLoading() || isSaving() || !canUpdate()"
                [loading]="isSaving()"
                [pTooltip]="canUpdate() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                tooltipPosition="top"
                (onClick)="faviconUploadInput.click()"></p-button>
              @if (faviconMarkedForRemoval()) {
                <p-button
                  [label]="'SETTINGS_GENERAL.KEEP_FAVICON' | translate"
                  icon="pi pi-undo"
                  severity="secondary"
                  [outlined]="true"
                  [disabled]="isLoading() || isSaving() || !canUpdate()"
                  [pTooltip]="canUpdate() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="undoRemoveFavicon()"></p-button>
              } @else if (currentFaviconUrl()) {
                <p-button
                  [label]="'SETTINGS_GENERAL.REMOVE_FAVICON' | translate"
                  icon="pi pi-trash"
                  severity="danger"
                  [outlined]="true"
                  [disabled]="isLoading() || isSaving() || !canUpdate()"
                  [pTooltip]="canUpdate() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
                  tooltipPosition="top"
                  (onClick)="removeFavicon()"></p-button>
              }
            </div>
            @if (pendingFaviconFile()) {
              <div class="branding-selection-row">
                <span>{{ pendingFaviconFile()?.name }}</span>
                <p-button
                  icon="pi pi-times"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  [disabled]="isLoading() || isSaving() || !canUpdate()"
                  [pTooltip]="
                    canUpdate()
                      ? ('GENERAL.CLEAR' | translate)
                      : ('GENERAL.NO_PERMISSION' | translate)
                  "
                  tooltipPosition="top"
                  (onClick)="clearPendingFaviconSelection()"></p-button>
              </div>
            }
          </div>
        </div>
      </div>

      <div class="settings-section capability-section">
        <span class="settings-page__label">
          {{ 'SETTINGS_GENERAL.FEATURE_FLAGS' | translate }}
        </span>
        <p class="description">
          {{ 'SETTINGS_GENERAL.FEATURE_FLAGS_DESCRIPTION' | translate }}
        </p>

        @if (featureFlagsLoadFailed()) {
          <p class="capability-empty">
            {{ 'SETTINGS_GENERAL.FEATURE_FLAGS_UNAVAILABLE' | translate }}
          </p>
        } @else if (!featureFlagsLoaded()) {
          <p class="capability-empty">
            {{ 'SETTINGS_GENERAL.FEATURE_FLAGS_LOADING' | translate }}
          </p>
        } @else if (enabledFeatureFlagPills().length > 0) {
          <div
            class="capability-pill-list"
            [attr.aria-label]="'SETTINGS_GENERAL.ENABLED_FEATURE_FLAGS' | translate">
            @for (featureFlag of enabledFeatureFlagPills(); track featureFlag.key) {
              <span class="capability-pill">
                @if (featureFlag.labelKey) {
                  {{ featureFlag.labelKey | translate }}
                } @else {
                  {{ featureFlag.label }}
                }
              </span>
            }
          </div>
        } @else {
          <p class="capability-empty">
            {{ 'SETTINGS_GENERAL.NO_FEATURE_FLAGS' | translate }}
          </p>
        }
      </div>

      <div class="settings-section capability-section">
        <span class="settings-page__label">
          {{ 'SETTINGS_GENERAL.PLUGINS' | translate }}
        </span>
        <p class="description">
          {{ 'SETTINGS_GENERAL.PLUGINS_DESCRIPTION' | translate }}
        </p>

        @if (pluginsLoadFailed()) {
          <p class="capability-empty">
            {{ 'SETTINGS_GENERAL.PLUGINS_UNAVAILABLE' | translate }}
          </p>
        } @else if (!pluginsLoaded()) {
          <p class="capability-empty">
            {{ 'SETTINGS_GENERAL.PLUGINS_LOADING' | translate }}
          </p>
        } @else if (enabledPluginPills().length > 0) {
          <div
            class="capability-pill-list"
            [attr.aria-label]="'SETTINGS_GENERAL.ENABLED_PLUGINS' | translate">
            @for (plugin of enabledPluginPills(); track plugin.key) {
              <span class="capability-pill">{{ plugin.label }}</span>
            }
          </div>
        } @else {
          <p class="capability-empty">
            {{ 'SETTINGS_GENERAL.NO_PLUGINS' | translate }}
          </p>
        }
      </div>
      </div>
    }
  </div>
  @if (!isLoading() && !loadError()) {
    <tf-settings-actions
      (cancelled)="cancel()"
      (save)="save()"
      [disabled]="
        !canUpdate() ||
        tenantNameControl.invalid ||
        languageControl.invalid ||
        timezoneControl.invalid ||
        isSaving()
      "
      [loading]="isSaving()"
      [saveTooltip]="canUpdate() ? '' : ('GENERAL.NO_PERMISSION' | translate)" />
  }
</div>
